import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Check, Pencil, UserPlus, Users, X } from 'lucide-react';
import { api, apiGet, apiPatch } from '../../lib/api.js';
import { useAuth } from '../../store/auth.js';
import { ownTeamPrompt, teamAdminPrompt } from '../../lib/teamHierarchy.js';
import ConfirmDialog from '../ui/ConfirmDialog.jsx';
import AddMemberModal from './AddMemberModal.jsx';
import AgentRoster from './AgentRoster.jsx';

const QK = ['team-downline'];
const errOf = (e, f) => e?.response?.data?.error || f;

/**
 * "My Team": editable team name, agent count, Add Team Member, and the roster of
 * everyone below the signed-in agent (all levels).
 * @returns {JSX.Element}
 */
export default function MyTeamPanel() {
  const qc = useQueryClient();
  const agent = useAuth((s) => s.agent);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [adding, setAdding] = useState(false);
  const [adminAsk, setAdminAsk] = useState(null); // {item, next}
  const [ownAsk, setOwnAsk] = useState(null); // {item, next}
  const [removeAsk, setRemoveAsk] = useState(null); // item
  const [leadAction, setLeadAction] = useState('reassign_to_supervisor');

  const { data } = useQuery({ queryKey: QK, queryFn: () => apiGet('/team/downline'), staleTime: 15_000 });
  const items = data?.items || [];
  const teamName = data?.teamName || 'My Agency Team';
  const refresh = () => qc.invalidateQueries({ queryKey: QK });
  // The server decides who may invite and where they can place people (it enforces it too).
  const { data: inviteOpts } = useQuery({ queryKey: ['team-invite-options'], queryFn: () => apiGet('/team/invite-options'), staleTime: 15_000 });
  const hasUpline = !!(agent?.uplineId || agent?.parentUserId);
  const canInvite = inviteOpts ? inviteOpts.canInvite : (!hasUpline || agent?.isTeamAdmin || ['admin', 'superadmin'].includes(agent?.role));

  const rename = useMutation({
    mutationFn: (teamName) => apiPatch('/team/name', { teamName }),
    onSuccess: () => { setEditing(false); refresh(); toast.success('Team renamed'); },
    onError: (e) => toast.error(errOf(e, 'Could not rename the team')),
  });
  const toggleAdmin = useMutation({
    mutationFn: ({ item, next }) => apiPatch(`/team/agents/${item._id}/team-admin`, { isTeamAdmin: next }),
    onSuccess: (_r, { next }) => { setAdminAsk(null); refresh(); toast.success(next ? 'Team Admin granted' : 'Team Admin revoked'); },
    onError: (e) => toast.error(errOf(e, 'Could not update Team Admin')),
  });
  const toggleOwnTeam = useMutation({
    mutationFn: ({ item, next }) => apiPatch(`/team/agents/${item._id}/invite-own-team`, { canInviteOwnTeam: next }),
    onSuccess: (_r, { next }) => { setOwnAsk(null); refresh(); toast.success(next ? 'Can now add their own team' : 'Own-team invites turned off'); },
    onError: (e) => toast.error(errOf(e, 'Could not update the permission')),
  });
  const removeAgent = useMutation({
    // DELETE with a JSON body ({leadAction}); apiDel would send it as query params.
    mutationFn: (item) => api.delete(`/team/agents/${item._id}`, { data: { leadAction } }).then((r) => r.data),
    onSuccess: (r) => { setRemoveAsk(null); refresh(); toast.success(`Agent removed${r.leadsMoved ? ` · ${r.leadsMoved} lead(s) moved` : ''}`); },
    onError: (e) => toast.error(errOf(e, 'Could not remove the agent')),
  });

  return (
    <section className="panel p-6 space-y-5" data-testid="my-team">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <span className="w-11 h-11 rounded-xl bg-civix-purple/20 border border-civix-purple/40 flex items-center justify-center shrink-0">
            <Users className="w-6 h-6 text-civix-purpleSoft" />
          </span>
          {editing ? (
            <form className="flex items-center gap-2" onSubmit={(e) => { e.preventDefault(); if (draft.trim()) rename.mutate(draft.trim()); }}>
              <input autoFocus value={draft} maxLength={80} onChange={(e) => setDraft(e.target.value)} aria-label="Team name" className="bg-civix-panelLight border border-civix-border rounded-lg px-3 py-1.5 text-lg font-display" />
              <button type="submit" aria-label="Save team name" className="text-emerald-400"><Check className="w-5 h-5" /></button>
              <button type="button" aria-label="Cancel rename" onClick={() => setEditing(false)} className="text-white/50"><X className="w-5 h-5" /></button>
            </form>
          ) : (
            <h2 className="font-display text-2xl font-semibold truncate" data-testid="team-name">
              {teamName}
              <button type="button" aria-label="Rename team" onClick={() => { setDraft(teamName); setEditing(true); }} className="ml-2 align-middle text-civix-purpleSoft hover:text-white"><Pencil className="w-4 h-4" /></button>
            </h2>
          )}
        </div>
        <div className="text-right">
          <div className="text-xs text-white/50">Agent Count</div>
          <div className="text-2xl font-semibold" data-testid="agent-count">{data?.agentCount ?? 0}<span className="text-xs text-white/50 ml-1.5">Active Agents</span></div>
        </div>
      </header>

      <p className="text-sm text-white/65 max-w-3xl">
        Track your team's performance by adding agents under you. You can see everyone below you — including the agents your recruits add.
        Team Admins add users to your team on your behalf; "Can add own team" lets an agent also build a team under themselves.
      </p>

      {canInvite && (
        <button type="button" onClick={() => setAdding(true)} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-civix-purple text-white text-sm font-bold hover:bg-civix-purpleDeep">
          <UserPlus className="w-4 h-4" /> Add Team Member
        </button>
      )}

      <AgentRoster items={items} onToggleAdmin={(item, next) => setAdminAsk({ item, next })} onToggleOwnTeam={(item, next) => setOwnAsk({ item, next })} onRemove={setRemoveAsk} />

      <AddMemberModal open={adding} onClose={() => setAdding(false)} onInvited={refresh} options={inviteOpts?.options || []} />

      <ConfirmDialog
        open={!!ownAsk}
        message={ownAsk && ownTeamPrompt(ownAsk.next, ownAsk.item.name)}
        confirmLabel={ownAsk?.next ? 'Confirm (Allow)' : 'Confirm (Turn off)'}
        busy={toggleOwnTeam.isPending}
        onCancel={() => setOwnAsk(null)}
        onConfirm={() => toggleOwnTeam.mutate(ownAsk)}
      />

      <ConfirmDialog
        open={!!adminAsk}
        message={adminAsk && teamAdminPrompt(adminAsk.next, adminAsk.item.name)}
        confirmLabel={adminAsk?.next ? 'Confirm (Make Team Admin)' : 'Confirm (Revoke)'}
        busy={toggleAdmin.isPending}
        onCancel={() => setAdminAsk(null)}
        onConfirm={() => toggleAdmin.mutate(adminAsk)}
      />

      <ConfirmDialog
        open={!!removeAsk}
        danger
        message={removeAsk && `Are you sure you want to remove ${removeAsk.name} from your team?`}
        confirmLabel="Remove Agent"
        busy={removeAgent.isPending}
        onCancel={() => setRemoveAsk(null)}
        onConfirm={() => removeAgent.mutate(removeAsk)}
      >
        <fieldset className="space-y-2 text-sm text-white/80">
          <legend className="text-xs text-white/50 mb-1">Their active leads:</legend>
          {[['reassign_to_supervisor', 'Reassign leads to me'], ['archive', 'Archive leads (unassign, keep history)']].map(([v, label]) => (
            <label key={v} className="flex items-center gap-2 cursor-pointer">
              <input type="radio" name="leadAction" className="accent-civix-purple" checked={leadAction === v} onChange={() => setLeadAction(v)} /> {label}
            </label>
          ))}
        </fieldset>
      </ConfirmDialog>
    </section>
  );
}
